import React from "react";
import { BRAND } from "../utils/brand";

/**
 * AthosFX logo (Brand Book v1.3, section 4: "Name as Logo").
 *
 * Wordmark  - the primary logo. "Athos" in Cormorant Garamond SemiBold + "FX" in Inter Medium
 *             at ~60% height, same baseline, always one word on one line.
 * Symbol    - the 3-peak mountain mark, used only where the name will not fit
 *             (collapsed sidebar, favicon, anything under ~100px wide).
 *
 * Styles live in public/assets/css/athosfx-theme.css (.athos-wordmark).
 *
 * LogoImage - the designer's logo artwork. This is what the panel shows.
 *             Wordmark and Symbol below are the text/drawn versions, kept as a fallback.
 */

// Logo artwork in public/assets/images/logo:
//   athosfx-logo-light.png / athosfx-logo-dark.png ... original files from the designer
//   athosfx-logo-<tone>-trim.png ...................... same logo without the empty margin (used here)
//   athosfx-symbol-<tone>.png ......................... the mountain mark only, for small spaces
// When the designer sends new artwork, replace the originals and re-create the trimmed copies.
const LOGO_DIR = "/assets/images/logo";

// tone: "light" = teal logo for light backgrounds | "dark" = ivory logo for teal/dark backgrounds
// type: "full" = mark + name | "symbol" = mark only (collapsed sidebar, anything under ~100px wide)
export function LogoImage({ tone = "light", type = "full", height = 34, className = "", style = {} }) {
  const file = type === "symbol" ? `athosfx-symbol-${tone}.png` : `athosfx-logo-${tone}-trim.png`;
  return (
    <img
      // src={`/assets/images/logo/athosfx-logo-light-trim.png`}
      src={`${LOGO_DIR}/${file}`}
      alt="AthosFX"
      height={height}
      className={`notranslate ${className}`}
      translate="no"
      draggable={false}
      style={{ width: "auto", maxWidth: "100%", verticalAlign: "middle", ...style }}
    />
  );
}

// variant: "main" (teal on ivory) | "reversed" (ivory on teal) | "one-colour" (charcoal on light)
export function Wordmark({ variant = "main", size = 28, className = "", style = {} }) {
  const variantClass = variant === "main" ? "" : `athos-wordmark--${variant}`;
  return (
    <span
      className={`athos-wordmark notranslate ${variantClass} ${className}`}
      translate="no"
      role="img"
      aria-label="AthosFX"
      style={{ fontSize: size, ...style }}
    >
      <span className="athos-wordmark__athos">Athos</span>
      <span className="athos-wordmark__fx">FX</span>
    </span>
  );
}

// Placeholder mountain mark until the final symbol artwork is supplied.
// variant: "main" (teal peaks) | "reversed" (ivory peaks, for teal backgrounds)
export function Symbol({ variant = "main", size = 28, className = "" }) {
  const peaks = variant === "reversed" ? BRAND.ivory : BRAND.teal;
  return (
    <svg
      className={`athos-symbol ${className}`}
      width={size}
      height={size}
      viewBox="0 0 48 48"
      role="img"
      aria-label="AthosFX"
    >
      <path d="M2 36 L15 17 L21 26 L29 9 L46 36 Z" fill={peaks} />
      <path d="M2 40 H46" stroke={BRAND.gold} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Header block for the Login / Register / Forgot / Reset password cards:
 * the teal logo centred on a white card, the tagline, a gold accent line, then the page title.
 */
export function AuthHeader({ title, subtitle }) {
  return (
    <div
      className="athos-auth-header"
      style={{ borderTopLeftRadius: "inherit", borderTopRightRadius: "inherit" }}
    >
      <LogoImage tone="light" height={56} />
      <div className="athos-auth-header__tagline">Steady Ground for Global Traders</div>
      <hr className="athos-accent-line" />
      <h5 className="athos-auth-header__title">{title}</h5>
      {subtitle && <p className="athos-auth-header__subtitle">{subtitle}</p>}
    </div>
  );
}

export default Wordmark;
